import {Component} from 'react';
import PropTypes from 'prop-types';

import {Divider, Form, Grid, Header, Input, Segment} from 'semantic-ui-react';
import fromPairs from 'lodash/fromPairs';
import keys from 'lodash/keys';
import './CustomiseSummary.scss';
import {isEqual} from 'lodash';
import Formatter from 'lib/Formatter';
import {getSaleAvm} from 'lib/PropertyUtils';
import PropertyDetailImages from 'lib/PropertyDetailImages';

import {presetDimensions} from 'components/imaging/ResizedDasWebImage';
import {PropertyImageGalleryContainer} from 'components/PropertyImageGallery';
import EditableTitle from 'components/EditableTitle';
import EditableFormSection from 'components/EditableFormSection';
import SummaryNotes from 'components/SummaryNotes';
import PropertyAvmDisplay from 'components/PropertyAvmDisplay';
import LoadingPanel from 'components/LoadingPanel/LoadingPanel';

export class CustomiseSummary extends Component {
  static propTypes = {
    propertyDetail: PropTypes.shape({
      intelliValAvm: PropTypes.object,
      propertyAvm: PropTypes.object,
      propertyId: PropTypes.number,
      rentalAvm: PropTypes.object,
      propertyPhotoList: PropTypes.arrayOf(PropTypes.string).isRequired,
      selectedImages: PropTypes.arrayOf(PropTypes.string).isRequired,
      singleLineAddress: PropTypes.string.isRequired,
    }).isRequired,
    isAvmFetching: PropTypes.bool,
    reportType: PropTypes.string.isRequired,
    section: PropTypes.object.isRequired,
    updatePreferences: PropTypes.func.isRequired,
    updatePropertyPreferences: PropTypes.func.isRequired,
    updateSectionValue: PropTypes.func.isRequired,
    onImageChange: PropTypes.func.isRequired,
    onUpload: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      showNotification: false,
      galleryIndexes: [ null, null, null, null, ],
      userIndexing: [false, false, false, false, ],
    };
  }

  componentDidMount() {
    this.recalculateGalleryIndexes();
  }

  componentDidUpdate(prevProps) {
    const {propertyDetail: newPropertyDetail} = this.props;
    const {propertyDetail: oldPropertyDetail} = prevProps;

    if (!isEqual(
      PropertyDetailImages.summaryPropertyPhotoList(newPropertyDetail),
      PropertyDetailImages.summaryPropertyPhotoList(oldPropertyDetail),
    ) || !isEqual(
      PropertyDetailImages.list(newPropertyDetail),
      PropertyDetailImages.list(oldPropertyDetail),
    )) {
      this.recalculateGalleryIndexes();
    }
  }

  recalculateGalleryIndexes = () => {
    const {propertyDetail} = this.props;
    let {galleryIndexes, userIndexing} = this.state;

    const actualList = PropertyDetailImages.list(propertyDetail);
    const selections = [...(propertyDetail.selectedImages || []), null, null, null, null].slice(0, 4);

    for (let i = 0; i < galleryIndexes.length; i++) {
      let index = -1;
      if (selections[i]) {
        index = actualList.findIndex(url => url === selections[i]);
      }
      else if (userIndexing[i]) {
        if (galleryIndexes[i] < 0 || galleryIndexes[i] > actualList.length) {
          index = actualList.length;
        }
      }
      else if (i < actualList.length) {
        index = i;
      }
      galleryIndexes[i] = index < 0 ? actualList.length : index;
    }
    this.setState({
      galleryIndexes,
    });
  };

  preventFormSubmit = (event) => {
    event.preventDefault();
  };

  getAvm = () => {
    const {propertyDetail, reportType} = this.props;
    return reportType === 'rental' ? propertyDetail.rentalAvm : getSaleAvm(propertyDetail);
  };

  onChange = (data) => {
    const {reportType, updateSectionValue, updatePreferences, updatePropertyPreferences} = this.props;

    keys(data.payload).forEach(key => {
      const keyPath = data.key ? `${data.key}.${key}` : key;
      updateSectionValue(data.payload[key], keyPath);

      if (key !== 'title') {
        const subKeyPath = data.key ? `.${data.key}` : '';
        const newKeyPath = `summary.sectionData${subKeyPath}.${key}`;
        const preference = fromPairs([[newKeyPath, data.payload[key]]]);
        updatePropertyPreferences(preference);
      }
    });

    if ('title' in data.payload) {
      const subKeyPath = data.key ? `.${data.key}` : '';
      const keyPath = `summary.sectionData${subKeyPath}.title`;
      const preference = fromPairs([[keyPath, data.payload.title]]);
      updatePreferences(preference, reportType);
    }
  };

  onIndexChange = (galleryIndex) => (index) => {
    const {onImageChange, propertyDetail} = this.props;
    let {galleryIndexes, userIndexing} = this.state;
    const propertyImages = PropertyDetailImages.list(propertyDetail);

    let nextIndex = index;
    if (nextIndex < 0) {
      nextIndex = propertyImages.length;
    }
    if (nextIndex > propertyImages.length) {
      nextIndex = 0;
    }

    galleryIndexes[galleryIndex] = nextIndex;
    userIndexing[galleryIndex] = true;
    this.setState({
      galleryIndexes,
      userIndexing,
    });

    onImageChange(galleryIndex, propertyImages[nextIndex]);
  };

  onUpload = (galleryIndex) => (files) => {
    let {userIndexing} = this.state;
    userIndexing[galleryIndex] = true;
    this.setState({
      userIndexing,
    });
    this.props.onUpload(files, galleryIndex);
  };

  onCopy = () => {
    const {updateSectionValue} = this.props;
    const propertyAvm = this.getAvm();
    const priceRange = `${Formatter.currency(propertyAvm.lowEstimate)} - ${Formatter.currency(propertyAvm.highEstimate)}`;
    updateSectionValue(priceRange, 'appraisalPriceRange.value');
  };

  render() {
    const {isAvmFetching, propertyDetail, section, reportType} = this.props;
    const {galleryIndexes} = this.state;
    const propertyImages = PropertyDetailImages.list(propertyDetail);

    const appraisalPriceRange = section.sectionData.appraisalPriceRange;
    const description = section.sectionData.description;
    const isRental = reportType === 'rental';

    return (
      <div className="summary">
        <Header as="h2" className="section-type" style={{fontWeight: 'bold'}} disabled>Summary</Header>
        <Segment style={{padding: '30px'}}>
          <Form className="summaryTitle" onSubmit={this.preventFormSubmit}>
            <Form.Group inline>
              <EditableTitle
                onUpdateData={(data) => {
                  this.onChange({
                    key: undefined,
                    payload: data
                  });
                }}
                width="50%"
                maxLength={32}
                title={section.sectionData.title}
                isEnabled={true}
              />
            </Form.Group>
          </Form>
          <h3 className="address">{propertyDetail.singleLineAddress}</h3>
          <Segment basic style={{marginRight: '80px', paddingLeft: '0'}}>
            <Grid>
              <Grid.Row>
                <Grid.Column width={12} className="primary-image">
                  <PropertyImageGalleryContainer
                      currentIndex={galleryIndexes[0]}
                      hasModal={false}
                      images={propertyImages}
                      inlineGalleryProps={{
                        style: {
                          maxHeight: 482,
                          width: 'auto',
                        },
                      }}
                      propertyId={propertyDetail.propertyId}
                      resizeProps={{isResize: true, resizeDimensions: presetDimensions.SUMMARY_PRIMARY}}
                      onIndexChange={this.onIndexChange(0)}
                      onUpload={this.onUpload(0)}
                  />
                </Grid.Column>
                <Grid.Column width={4} className="secondary-images" style={{
                  paddingLeft: '3px',
                  paddingRight: '22px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}>
                  {[1, 2, 3].map(i => (
                    <PropertyImageGalleryContainer currentIndex={galleryIndexes[i]}
                                                   hasModal={false}
                                                   images={propertyImages}
                                                   inlineGalleryProps={{
                                                     style: {
                                                       maxHeight: 154,
                                                     },
                                                   }}
                                                   key={i}
                                                   propertyId={propertyDetail.propertyId}
                                                   resizeProps={{
                                                     isResize: true,
                                                     resizeDimensions: presetDimensions.SUMMARY_SECONDARY
                                                   }}
                                                   onIndexChange={this.onIndexChange(i)}
                                                   onUpload={this.onUpload(i)}/>
                  ))}
                </Grid.Column>
              </Grid.Row>
            </Grid>
            <Divider hidden/>
            <div className="appraisal-container">

              <Form className="appraisal" onSubmit={this.preventFormSubmit}>
                <EditableFormSection sectionData={appraisalPriceRange}
                                     valueElementProps={{
                                       control: Input,
                                       maxLength: '70',
                                       placeholder: 'Enter appraisal price range',
                                       width: '10',
                                     }}
                                     onUpdateData={(data) => this.onChange({
                                       key: 'appraisalPriceRange',
                                       payload: data
                                     })}/>
              </Form>
              <div className="property-estimate">
                <LoadingPanel isLoading={isAvmFetching}>
                  <PropertyAvmDisplay
                    isRental={isRental}
                    propertyAvm={this.getAvm()}
                    onCopyToReport={this.onCopy}/>
                </LoadingPanel>
              </div>
            </div>
            <Divider hidden/>
            <SummaryNotes
              enabled={description.isEnabled}
              title={description.title}
              value={description.value}
              onChange={(field, value) => this.onChange({
                key: 'description',
                payload: {[field]: value},
              })}
            />
          </Segment>
        </Segment>
      </div>
    );
  }
}
